/**
 * PTW (Permit to Work) API Service
 * Connects to Django backend at /api/om/ptw*
 */
import backendApiService from './backendApi';

const BASE = '/om';

// ─── Permit CRUD ──────────────────────────────────────────────────────────────

export const ptwApi = {
  /** List permits for a site */
  list: (siteCode, params = {}) =>
    backendApiService.request(`${BASE}/ptw/`, {
      method: 'GET',
      params: { site_code: siteCode, ...params },
    }),

  /** Get single permit detail (fully nested) */
  detail: (id) =>
    backendApiService.request(`${BASE}/ptw/${id}/full-detail/`, { method: 'GET' }),

  /** Create a new permit with auto-JHA, PPE, Tools, Isolation */
  create: (data) =>
    backendApiService.request(`${BASE}/ptw/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Partial update a permit */
  update: (id, data) =>
    backendApiService.request(`${BASE}/ptw/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // ── Workflow Actions ────────────────────────────────────────────────────────

  submitForReview: (id) =>
    backendApiService.request(`${BASE}/ptw/${id}/submit-for-review/`, { method: 'POST' }),

  spApproveJHA: (id, signatureText, notes = '', decision = 'approve') =>
    backendApiService.request(`${BASE}/ptw/${id}/sp-approve-jha/`, {
      method: 'POST',
      body: JSON.stringify({ signature_text: signatureText, notes, decision }),
    }),

  scPrepare: (id, signatureText, notes = '') =>
    backendApiService.request(`${BASE}/ptw/${id}/sc-prepare/`, {
      method: 'POST',
      body: JSON.stringify({ signature_text: signatureText, notes }),
    }),

  piIssue: (id, signatureText, keySafeNumber = '') =>
    backendApiService.request(`${BASE}/ptw/${id}/pi-issue/`, {
      method: 'POST',
      body: JSON.stringify({ signature_text: signatureText, key_safe_number: keySafeNumber }),
    }),

  conductTBT: (id, tbtData) =>
    backendApiService.request(`${BASE}/ptw/${id}/conduct-tbt/`, {
      method: 'POST',
      body: JSON.stringify(tbtData),
    }),

  suspend: (id, reason) =>
    backendApiService.request(`${BASE}/ptw/${id}/suspend/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  resume: (id) =>
    backendApiService.request(`${BASE}/ptw/${id}/resume/`, { method: 'POST' }),

  surrender: (id, signatureText, notes = '') =>
    backendApiService.request(`${BASE}/ptw/${id}/surrender/`, {
      method: 'POST',
      body: JSON.stringify({ signature_text: signatureText, notes }),
    }),

  close: (id, signatureText) =>
    backendApiService.request(`${BASE}/ptw/${id}/close/`, {
      method: 'POST',
      body: JSON.stringify({ signature_text: signatureText }),
    }),

  cancel: (id, reason) =>
    backendApiService.request(`${BASE}/ptw/${id}/cancel/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  initiateHandover: (id, incomingPrId) =>
    backendApiService.request(`${BASE}/ptw/${id}/initiate-handover/`, {
      method: 'POST',
      body: JSON.stringify({ incoming_pr_id: incomingPrId }),
    }),

  completeHandover: (id, handoverId, signatureText) =>
    backendApiService.request(`${BASE}/ptw/${id}/complete-handover/`, {
      method: 'POST',
      body: JSON.stringify({ handover_id: handoverId, signature_text: signatureText }),
    }),

  auditTrail: (id) =>
    backendApiService.request(`${BASE}/ptw/${id}/audit-trail/`, { method: 'GET' }),
};

// ─── Work Orders ──────────────────────────────────────────────────────────────

export const ptwWorkOrderApi = {
  list: (siteCode) =>
    backendApiService.request(`${BASE}/ptw-work-orders/`, {
      method: 'GET',
      params: { site_code: siteCode },
    }),
  create: (data) =>
    backendApiService.request(`${BASE}/ptw-work-orders/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ─── JHA ──────────────────────────────────────────────────────────────────────

export const ptwJHAApi = {
  listForPermit: (permitId) =>
    backendApiService.request(`${BASE}/ptw-jha-items/?permit_id=${permitId}`, { method: 'GET' }),

  updateItem: (id, data) =>
    backendApiService.request(`${BASE}/ptw-jha-items/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  addItem: (data) =>
    backendApiService.request(`${BASE}/ptw-jha-items/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteItem: (id) =>
    backendApiService.request(`${BASE}/ptw-jha-items/${id}/`, { method: 'DELETE' }),

  templates: () =>
    backendApiService.request(`${BASE}/ptw-jha-templates/`, { method: 'GET' }),

  applyTemplate: (templateId, permitId) =>
    backendApiService.request(`${BASE}/ptw-jha-templates/${templateId}/apply-to-permit/`, {
      method: 'POST',
      body: JSON.stringify({ permit_id: permitId }),
    }),
};

// ─── PPE & Tools ──────────────────────────────────────────────────────────────

export const ptwPPEApi = {
  master: () =>
    backendApiService.request(`${BASE}/ptw-ppe-master/`, { method: 'GET' }),
  forPermit: (permitId) =>
    backendApiService.request(`${BASE}/ptw-jha-items/?permit_id=${permitId}`, { method: 'GET' }),
};

export const ptwToolsApi = {
  master: () =>
    backendApiService.request(`${BASE}/ptw-tools-master/`, { method: 'GET' }),
};

// ─── Isolation ────────────────────────────────────────────────────────────────

export const ptwIsolationApi = {
  listForPermit: (permitId) =>
    backendApiService.request(`${BASE}/ptw-isolation/?permit_id=${permitId}`, { method: 'GET' }),

  confirmIsolated: (itemId) =>
    backendApiService.request(`${BASE}/ptw-isolation/${itemId}/confirm-isolated/`, { method: 'POST' }),

  confirmDeIsolated: (itemId) =>
    backendApiService.request(`${BASE}/ptw-isolation/${itemId}/confirm-de-isolated/`, { method: 'POST' }),

  addItem: (data) =>
    backendApiService.request(`${BASE}/ptw-isolation/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ─── SLD Equipment ────────────────────────────────────────────────────────────

export const ptwSLDApi = {
  list: (siteCode) =>
    backendApiService.request(`${BASE}/ptw-sld-equipment/?site_code=${siteCode}`, { method: 'GET' }),
  create: (data) =>
    backendApiService.request(`${BASE}/ptw-sld-equipment/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ─── Roles ────────────────────────────────────────────────────────────────────

export const ptwRolesApi = {
  list: (siteCode) =>
    backendApiService.request(`${BASE}/ptw-roles/?site_code=${siteCode}`, { method: 'GET' }),
  assign: (data) =>
    backendApiService.request(`${BASE}/ptw-roles/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  remove: (id) =>
    backendApiService.request(`${BASE}/ptw-roles/${id}/`, { method: 'DELETE' }),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export const ptwNotificationsApi = {
  list: () =>
    backendApiService.request(`${BASE}/ptw-notifications/`, { method: 'GET' }),
  markRead: (id) =>
    backendApiService.request(`${BASE}/ptw-notifications/${id}/mark-read/`, { method: 'POST' }),
};

// ─── KPI ──────────────────────────────────────────────────────────────────────

export const ptwKPIApi = {
  summary: (siteCode) =>
    backendApiService.request(`${BASE}/ptw-kpi/summary/?site_code=${siteCode}`, { method: 'GET' }),
  list: (siteCode) =>
    backendApiService.request(`${BASE}/ptw-kpi/?site_code=${siteCode}`, { method: 'GET' }),
};
